import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { error, log } from '@/services/Log'
import { XsrfResilient } from '@/services/TypedHelpers'
import type { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type { StandardOrder } from '@/types/ShopFront/CheckoutStandards'

export const removeCartItem = XsrfResilient(async ({ id, order }: {
  id: string,
  order: StandardOrder
}) => {
  log('removeCartItem', { id, order })
  let success = true
  let newOrder: StandardOrder = { ...order }
  const [
    { default: Axios },
    { getCartFromResponse },
    { default: recoverShippingOptions },
    { standardizeAddressForBigC },
    { getWarrantyId },
    { goToCart },
    { getOrderStandardizer },
    { augmentConsignments },
    { standardizeAddressStorefrontCheckoutApi },
    { calculateTotalDiscountFromLineItems },
    { cartDeleteItem },
  ] = await allPromisesWithRetries(() => [
    import('axios'),
    import('./getCartFromResponse'),
    import('../shipmentServices/recoverShippingOptions'),
    import('@/services/Standardizers/standardizeAddressForBigC'),
    import('./getWarrantyId'),
    import('@/services/FramedCheckout/navigationServices'),
    import('@/services/Standardizers/standardizeOrder'),
    import('@/services/Standardizers/augmentConsignments'),
    import('@/services/Standardizers/standardizeAddressStorefrontCheckoutApi'),
    import('@/helpers/checkoutHelpers/calculateTotalDiscountFromLineItems'),
    import('@/services/Cart/cartDeleteItem'),
  ])
  const warrantyId: string | null = getWarrantyId({ lineItems: order?.cart?.lineItems, id })
  const shipment = order.shipments.find(({ lineItemIds }) => lineItemIds.indexOf(id) >= 0)
  // Deletes a Line Item from the Cart. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-cart-items/checkoutscartsitemsitemidbycheckoutidandcartiddelete
  try {
    const response = await cartDeleteItem({ itemId: id, cartId: order?.cart.id })

    if (!response.newCart) {
      log('removeCartItem is removing the last item in the cart navigating user to cart page')
      await goToCart()
      return { success: true, newOrder }
    }

    success = success && response.success
    if (success && response.newCart) {
      if (shipment?.selectedShippingOption) {
        const data = {
          shippingAddress: standardizeAddressForBigC(shipment?.shippingAddress),
          lineItems: shipment?.lineItems
            .filter(({ id: itemId }) => itemId !== id)
            .map(({ id: itemId, quantity }) => ({
              itemId,
              quantity: (
                response.newCart?.lineItems.find((item) => item.id === itemId)?.quantity || quantity
              ),
            })),
        }
        // check if this shipment will still exist
        if (shipment.lineItemIds.find((itemInShimpmentId) => (
          itemInShimpmentId !== warrantyId && itemInShimpmentId !== id
        ))) {
          try {
            const consignmentUpdateResponse = await Axios.put<BCCheckoutType>(
              `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`,
              data,
            )
            success = success && consignmentUpdateResponse?.status === 200
            const rawCart = consignmentUpdateResponse?.data?.cart
            const cart = getCartFromResponse(rawCart, order)
            const standardizeOrder = getOrderStandardizer({
              augmentConsignments,
              standardizeAddressStorefrontCheckoutApi,
              calculateTotalDiscountFromLineItems,
            })
            newOrder = await standardizeOrder(consignmentUpdateResponse?.data, cart)
            log('removeCartItem -> recoverShippingOptions', { id, order, newOrder })

            return await recoverShippingOptions({ order, newOrder, newCart: newOrder.cart })
          } catch (err) {
            error(`removeCartItem Consignment quantity update FAILED: ${String(err)}`)
            log({ success: false, newOrder })
            return { success: false, newOrder }
          }
        }
      }
      log('removeCartItem -> recoverShippingOptions', { id, order, newCart: response.newCart })
      if (!success) {
        log('removeCartItem FAILED', { success: false, newOrder })
        return { success: false, newOrder }
      }
      return await recoverShippingOptions({ order, newCart: response.newCart })
    }
  } catch (err) {
    log('removeCartItem FAILED', { success: false, url: `/api/storefront/carts/${order?.cart?.id}/items/${id}`, err })
  }
  log('removeCartItem FAILED', { success: false })
  return { success: false }
})

export default removeCartItem
